/** Load a debounced campus-safe preview for one automatic Kuali module scope. */
import { useEffect, useState } from 'react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { getKualiResearchSelectionPreview } from '../services/api'
import type {
  KualiResearchFeedOptions,
  KualiResearchModuleOptions,
  KualiResearchSelectionPreview,
} from '../types'

export function useKualiResearchSelectionPreview({
  api,
  orgId,
  vaultId,
  open,
  enabled,
  apiUrl,
  module,
  credentialSource,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  open: boolean
  enabled: boolean
  apiUrl: string
  module: KualiResearchModuleOptions
  credentialSource: KualiResearchFeedOptions['credentialSource']
}) {
  const [preview, setPreview] = useState<KualiResearchSelectionPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const validRequest = open
    && enabled
    && /^https:\/\/[^/]+\.kuali\.co\/res\/?$/.test(apiUrl)
    && Number.isInteger(module.limit)
    && module.limit > 0
    && module.filters.every(filter => filter.value.trim())

  useEffect(() => {
    if (!validRequest) {
      setPreview(null)
      setLoading(false)
      setError(undefined)
      return
    }
    let current = true
    setLoading(true)
    setError(undefined)
    const timer = window.setTimeout(() => {
      void getKualiResearchSelectionPreview(api, orgId, vaultId, {
        apiUrl,
        module,
        credentialSource,
      })
        .then(result => { if (current) setPreview(result) })
        .catch((requestError: any) => {
          if (!current) return
          const detail = requestError?.response?.data?.detail
          setError(typeof detail === 'string'
            ? detail
            : `Live ${module.source} preview is unavailable.`)
        })
        .finally(() => { if (current) setLoading(false) })
    }, 350)
    return () => {
      current = false
      window.clearTimeout(timer)
    }
  }, [api, apiUrl, credentialSource, module, orgId, validRequest, vaultId])

  return { preview, loading, error }
}
